import {faStar as faStarEmpty} from '@fortawesome/free-regular-svg-icons'
import {faStar as faStarSolid, faSyncAlt} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import * as R from 'ramda'
import * as React from 'react'
import ReactModal from 'react-modal'
import {cardsInDeck} from '@kapitalist/contract/ruleset'
import {show$} from '@kapitalist/contract/utils/millions/copy'
import {safely, safelyOr} from '@kapitalist/utils/safely'
import DSButton from '~/design-system/Button'
import DSText, {DSTextDiv} from '~/design-system/DSText'
import {cardStarRating} from '~/game/types'
import {cardFactory} from '~/models/game/factories'
import CardImage from '~/pages/GameLobby/GameCard/stateless'
import {joinElements} from '~/pages/utils'
import {useDispatch, useSelector} from '~/redux/hooks'
import {setInspectingCard} from '~/redux/reducers/ui'
import {AlternateStateFontAwesomeIcon} from '~/utils/FontAwesome/HoverableFontAwesomeIcon'
import styles from './styles.module.css'

const InfoGridItemData = (props: {children: React.ReactNode}) => (
  <DSTextDiv size="label-demi" color="white" className={styles.infoGridItemData}>
    {props.children}
  </DSTextDiv>
)

const InfoGridItemTitle = (props: {children: React.ReactNode}) => (
  <DSText size="label" color="white" className={styles.infoGridItemTitle}>
    {props.children}
  </DSText>
)

const Star = (props: {filled: boolean}) => (
  <AlternateStateFontAwesomeIcon
    icon={{
      icon: faStarEmpty,
      style: {
        width: '15px',
        height: '15px',
      },
      className: styles.starIcon,
    }}
    alternateIcon={{
      icon: faStarSolid,
    }}
    alternateState={props.filled}
  />
)

const renderStars = (starRating: number) =>
  joinElements([
    ...R.repeat(<Star filled />, starRating),
    ...R.repeat(<Star filled={false} />, Math.max(0, 5 - starRating)),
  ])

function CardInspectionModal() {
  const inspectingCard = useSelector(state => state.uiState.inspectingCard)
  const skin = useSelector(state => state.settingsState.skin)
  const dispatch = useDispatch()

  // NB: modal mounts before `inspectingCard` is usually set, but this doesn't hurt
  const [flipped, setFlipped] = React.useState(inspectingCard?.flipped ?? false)

  const card = safely(inspectingCard?.data, cardFactory)

  // start each newly-inspected card in the flipped state it had where inspected
  const [lastCardId, setLastCardId] = React.useState(card?.id)
  if (card?.id !== lastCardId) {
    setLastCardId(card?.id)
    setFlipped(inspectingCard?.flipped ?? false)
  }

  const requestClose = () => dispatch(setInspectingCard(undefined))

  return (
    <ReactModal
      // we toggle this instead of unmounting to avoid strict mode issues
      // where the body blur would remain applied after closing (in dev)
      isOpen={!!card}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      onRequestClose={requestClose}
      overlayClassName={styles.overlay}
      className={styles.modalWrapper}
      bodyOpenClassName={styles.body}
    >
      {card && (
        <div className={styles.modalBackground}>
          <div className={styles.cardImageAndButtonWrapper}>
            <CardImage
              className={styles.cardImage}
              model={card}
              invert={flipped}
              skin={skin}
            />
            {card.isFlippable() && (
              <div
                className={styles.flipIcon}
                onClick={() => setFlipped(prev => !prev)}
              >
                <FontAwesomeIcon icon={faSyncAlt} />
              </div>
            )}
          </div>
          <div className={styles.cardDescriptionWrapper}>
            <DSTextDiv
              size="body-17-demi"
              color="white"
              className={styles.cardTitle}
            >
              {card.displayName(skin)}
            </DSTextDiv>
            <DSTextDiv
              size="body-15"
              color="white"
              className={styles.cardDescriptionText}
            >
              {card.shortDescription(skin)}
            </DSTextDiv>

            <div className={styles.infoGrid}>
              <InfoGridItemTitle>Number in deck</InfoGridItemTitle>
              <InfoGridItemData>{cardsInDeck[card.type]}</InfoGridItemData>
              <InfoGridItemTitle>Cash value</InfoGridItemTitle>
              <InfoGridItemData>
                {safelyOr(card.value, show$, 'N/A')}
              </InfoGridItemData>
              <InfoGridItemTitle>Rating</InfoGridItemTitle>
              <InfoGridItemData>
                {renderStars(cardStarRating[card.type])}
              </InfoGridItemData>
            </div>

            <div className={styles.flexSpacer} />

            <DSButton
              size="17px"
              onClick={requestClose}
              buttonStyle="tableAction"
              className={styles.dismissButton}
            >
              Dismiss
            </DSButton>
          </div>
        </div>
      )}
    </ReactModal>
  )
}

export default CardInspectionModal
